<template>
  <div class="overview-hero flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
    <div class="flex flex-col gap-1 min-w-0">
      <h1 class="overview-hero__title m-0 truncate" data-testid="overview-hero-title">
        {{ projectName }}
      </h1>
      <p class="overview-hero__subline m-0">
        {{ $t('UserProjects.overview.hero.subtitle', { date: formattedStartDate }) }}
      </p>
    </div>
    <div class="flex flex-wrap items-center gap-2 shrink-0 max-w-full">
      <button
        type="button"
        class="overview-hero__btn-primary btn-primary-accent"
        data-testid="overview-create-commitment"
        @click="goToCreateCommitment"
      >
        <i class="pi pi-plus text-sm" aria-hidden="true" />
        {{ $t('UserProjects.overview.hero.createCommitment') }}
      </button>
      <button
        type="button"
        class="overview-hero__btn-secondary"
        data-testid="overview-project-settings"
        @click="settingsVisible = true"
      >
        <i class="pi pi-cog text-sm" aria-hidden="true" />
        {{ $t('UserProjects.overview.hero.projectSettings') }}
      </button>
    </div>

    <ProjectSettingsDialog
      v-model:visible="settingsVisible"
      :project-id="selectedProject?.id || ''"
      :project-name="projectName"
      :users="participantUsers"
      :team-lookup="teamLookup"
      @saved="onSettingsSaved"
      @closed-requested="onClosureRequested"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ProjectSettingsDialog from './Settings/ProjectSettingsDialog.vue';

export default {
  name: 'OverviewHeroHeader',
  components: {
    ProjectSettingsDialog,
  },
  inject: {
    participantUsersProvider: {
      from: 'participantUsers',
      default: () => () => [],
    },
  },
  emits: ['settings-saved', 'closure-requested'],
  data() {
    return {
      settingsVisible: false,
    };
  },
  computed: {
    ...mapGetters('pmProject', {
      selectedProject: 'selectedProject',
    }),
    ...mapGetters({
      teamsForSelection: 'getTeamsForSelection',
    }),
    participantUsers() {
      const provider = this.participantUsersProvider;
      return typeof provider === 'function' ? provider() : [];
    },
    teamLookup() {
      const lookup = {};
      (this.teamsForSelection || []).forEach((team) => {
        lookup[String(team._id)] = team.name;
      });
      return lookup;
    },
    projectName() {
      return this.selectedProject?.name || '—';
    },
    formattedStartDate() {
      const value = this.selectedProject?.startDate;
      if (!value) return '—';
      return new Date(value).toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
  methods: {
    ...mapActions('pmProject', ['fetchProjects']),
    goToCreateCommitment() {
      this.$router.push({ path: '/user-projects/my-commitments', query: { create: '1' } });
    },
    async onSettingsSaved() {
      await this.fetchProjects({ scope: 'active' });
      this.$emit('settings-saved');
    },
    async onClosureRequested() {
      await this.fetchProjects({ scope: 'active' });
      this.$emit('closure-requested');
    },
  },
};
</script>

<style scoped>
.overview-hero__title {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--color-dark);
}

.overview-hero__subline {
  font-size: 0.875rem;
  color: var(--color-disabled);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.overview-hero__btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: normal;
  text-align: center;
  line-height: 1.3;
  max-width: 100%;
}

.overview-hero__btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  color: var(--color-dark);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: normal;
  text-align: center;
  line-height: 1.3;
  max-width: 100%;
}
</style>
